import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { getBankAccounts } from '../../services/BankAccountService';
import { sanitizeMultilineText } from '../../utils/sanitize';

const UpdateBillModal = ({ show, handleClose, editedBill, setEditedBill, handleSaveChanges, applyToAll, setApplyToAll }) => {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Determine if the bill is part of a series - bill_series_id is a UUID string
  const isPartOfSeries = editedBill.bill_series_id && typeof editedBill.bill_series_id === 'string' && editedBill.bill_series_id.trim() !== '';
  
  // Load bank accounts when the modal is shown
  useEffect(() => {
    const fetchBankAccounts = async () => {
      if (show) {
        setLoading(true);
        try {
          const accounts = await getBankAccounts();
          setBankAccounts(accounts);
        } catch (error) {
          console.error('Error fetching bank accounts:', error.name);
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchBankAccounts();
  }, [show]);
  
  // Handle description changes, preserving spaces and line breaks
  const handleDescriptionChange = (e) => {
    setEditedBill({
      ...editedBill,
      description: sanitizeMultilineText(e.target.value)
    });
  };
  
  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      dialogClassName="mobile-responsive-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Bill Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group controlId="formBillName">
            <Form.Label>Bill Name</Form.Label>
            <Form.Control
              type="text"
              value={editedBill.name}
              onChange={(e) => setEditedBill({ ...editedBill, name: e.target.value })}
            />
          </Form.Group>
          <Form.Group controlId="formBillAmount">
            <Form.Label>Amount</Form.Label>
            <Form.Control
              type="text"
              value={editedBill.amount}
              onChange={(e) => setEditedBill({ ...editedBill, amount: e.target.value })}
            />
          </Form.Group>
          <Form.Group controlId="formBillDescription">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={editedBill.description || ''}
              onChange={handleDescriptionChange}
              placeholder="Optional description"
            />
            <Form.Text className="text-muted">
              You can use spaces and line breaks (Enter) anywhere in this text.
            </Form.Text>
          </Form.Group>
          <Form.Group controlId="formBillDueDate">
            <Form.Label>Due Date</Form.Label>
            <Form.Control
              type="date"
              value={editedBill.due_date}
              onChange={(e) => setEditedBill({ ...editedBill, due_date: e.target.value })}
            />
          </Form.Group>
          <Form.Group controlId="formBankAccount">
            <Form.Label>Bank Account</Form.Label>
            <Form.Select
              value={editedBill.bank_account || ''}
              onChange={(e) => setEditedBill({ ...editedBill, bank_account: e.target.value ? parseInt(e.target.value, 10) : null })}
              disabled={loading}
            >
              <option value="">Select a Bank Account</option>
              {bankAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.nickname || account.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <br />
          <Form.Group controlId="formApplyToAll">
            <Form.Check
              type="checkbox"
              label={`Apply changes to all following Bills in series ${!isPartOfSeries ? '(Not available)' : ''}`}
              checked={applyToAll}
              onChange={(e) => setApplyToAll(e.target.checked)}
              disabled={!isPartOfSeries}
            />
            {applyToAll && !isPartOfSeries && (
              <Alert variant="warning" className="mt-2">
                This bill is not part of a recurring series, so changes cannot be applied to future bills.
              </Alert>
            )}
            {!isPartOfSeries && (
              <div className="text-muted small mt-1">
                This option is only available for bills that are part of a recurring series.
              </div>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSaveChanges}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateBillModal;
